import React from "react";
import { Col, Row } from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import Switch from "@mui/material/Switch";
import Langs, { Localize } from "../../../app/lang/langs";
import ImageButton from "../../buttons/ImageButton";
import addIcon from '../../../assets/images/addIconGray.png';
import addIconFocus from '../../../assets/images/addIconOrange.png';
import deleteIcon from '../../../assets/images/delete.png';
import deleteIconFocus from '../../../assets/images/delete-orange.png';
import { useDispatch } from "react-redux";
import { setAddMembersModalVisibility } from "../vcompSlice";
import TabNavigationButtons from "./TabNavigationButtons";
import moment from "moment";

/**
 * MembersTab - Renders the project members (collabs) DataGrid (case 4).
 * Includes MCQ toggle per collab, MCQ results link, add/remove members.
 */
export default function MembersTab({
    isMobile,
    mode,
    activeIndex,
    setActiveIndex,
    collabsList,
    dtCollabs,
    onConfirmDeleteCollab,
    onActivateCollabMcq,
    onActivateAllMcq,
    onViewMcqResults,
}) {
    const dispatch = useDispatch();

    const allActive = collabsList.every(collab => collab.active_mcq === 1);

    const columns = [
        {
            field: 'nom',
            headerName: Localize('nom'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'prenom',
            headerName: Localize('prenom'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'active_mcq',
            flex: 0.7,
            minWidth: 100,
            renderHeader: () => (
                <div className={`d-flex ${isMobile === 2 ? 'flex-column' : 'align-items-center'}`}>
                    <Langs str="MCQ" />
                    <div className={`${isMobile === 2 ? 'mt-1 justify-content-start' : 'ms-2 d-flex align-items-center justify-content-end'}`}>
                        <Switch
                            size="small"
                            checked={allActive}
                            onChange={(e) => onActivateAllMcq({ value: e.target.checked })}
                            color="warning"
                        />
                    </div>
                </div>
            ),
            renderCell: (params) => (
                <div className="d-flex justify-content-center">
                    <Switch
                        size="small"
                        checked={params.row.active_mcq == 1}
                        onChange={(e) => onActivateCollabMcq({ value: e.target.checked }, params.row)}
                        color="warning"
                    />
                </div>
            ),
            sortable: false,
        },
        {
            field: 'mcq_results',
            headerName: `MCQ ${Localize('Resultas') || 'Resultas'}`,
            flex: 1,
            minWidth: 130,
            sortable: false,
            renderCell: (params) => {
                if (params.row.last_response_date) {
                    return (
                        <p
                            style={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                color: 'blue',
                                margin: 0,
                            }}
                            onClick={() => onViewMcqResults(params.row.id, params.row.last_responses_number, params.row.last_response_date)}>
                            {moment(params.row.last_response_date).format('DD/MM/YYYY')}
                        </p>
                    );
                }
                return <p style={{ margin: 0 }}>Pas de réponse</p>;
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 70,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <ImageButton
                    image={deleteIcon}
                    imageFocus={deleteIconFocus}
                    onClick={() => {
                        onConfirmDeleteCollab(params.row.id);
                    }}
                    active={true}
                    width={32}
                />
            ),
        },
    ];

    return (
        <div>
            <div
                className={`card p-3 m-4 d-flex flex-column align-content-center border-0`}
                style={isMobile === 2 ? { height: '85%', display: 'flex', flexDirection: 'column' } : {}}
            >
                <Row className='d-flex justify-content-between w-100 mb-4 mt-2 ps-2'>
                    <Col lg={4} xs={8} className="my-auto">
                        <Langs str="EDIT_PROJECT_MEMBERS" />
                    </Col>
                    <Col lg={8} xs={4} className="d-flex justify-content-end pe-4 ">
                        <ImageButton
                            image={addIcon}
                            imageFocus={addIconFocus}
                            onClick={() => {
                                dispatch(setAddMembersModalVisibility(true));
                            }}
                            active={true}
                            width={32}
                        />
                    </Col>
                </Row>
                <Row className='d-flex justify-content-between w-100 mb-4 mt-2 ps-2'>
                    <Langs str={"Total"} />: {collabsList.length}
                </Row>
                <div style={{ width: '100%', height: isMobile === 2 ? 'max(320px, calc(90vh - 300px))' : 'max(400px, calc(100vh - 300px))' }}>
                    <DataGrid
                        rows={collabsList}
                        columns={columns}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        hideFooter
                        density="comfortable"
                        sx={{
                            height: '100%',
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                borderRadius: '8px 8px 0 0',
                            },
                            '& .MuiDataGrid-row:nth-of-type(odd)': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            },
                            '& .MuiDataGrid-cell': {
                                borderColor: 'rgba(0, 0, 0, 0.06)',
                            },
                        }}
                    />
                </div>
                {isMobile === 2 && (
                    <TabNavigationButtons
                        isMobile={isMobile}
                        mode={mode}
                        onPrevious={mode !== "edit" ? () => { setActiveIndex(activeIndex - 1) } : null}
                        onNext={() => { setActiveIndex(activeIndex + 1) }}
                        nextLabel={<Langs str="Next" />}
                        showPreviousInEdit={false}
                    />
                )}
            </div>
            {isMobile !== 2 && (
                <TabNavigationButtons
                    isMobile={isMobile}
                    mode={mode}
                    onPrevious={mode !== "edit" ? () => { setActiveIndex(activeIndex - 1) } : null}
                    onNext={() => { setActiveIndex(activeIndex + 1) }}
                    nextLabel={<Langs str="Next" />}
                    showPreviousInEdit={false}
                />
            )}
        </div>
    );
}
